import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, filter, map, of, take } from 'rxjs';
import {
  actions,
  bookLearningDomainQuery,
  feature,
  tocLearningDomainQuery,
} from './edu-content-toc-learning-domain.feature';

// Route data key. Set to `true` on routes that need the toc + descendants
// (e.g. a sub-overview that lists children); leave unset / `false` on routes
// that only need the toc itself (e.g. toc-detail).
export const LEARNING_DOMAINS_INCLUDE_CHILDREN_KEY = 'learningDomainsIncludeChildren';

// Route data key. Set to `true` on routes that need the toc + ancestors
// (e.g. a toc-detail that shows breadcrumbs with parent LDs); leave unset /
// `false` on routes that only need the toc itself.
export const LEARNING_DOMAINS_INCLUDE_ANCESTORS_KEY = 'learningDomainsIncludeAncestors';

const TOC_PARAM_ALIASES = ['eduContentTOCId', 'toc', 'tocId'] as const;
const BOOK_PARAM_ALIASES = ['eduContentBookId', 'book', 'bookId'] as const;

function readNumericParam(
  route: ActivatedRouteSnapshot,
  aliases: readonly string[]
): number | undefined {
  for (const alias of aliases) {
    const raw = route.paramMap.get(alias);
    if (raw == null) continue;
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function readTocId(route: ActivatedRouteSnapshot): number | undefined {
  return readNumericParam(route, TOC_PARAM_ALIASES);
}

function readBookId(route: ActivatedRouteSnapshot): number | undefined {
  return readNumericParam(route, BOOK_PARAM_ALIASES);
}

export const resolveLearningDomainsForToc: ResolveFn<boolean> = (route) => {
  const store = inject(Store);

  const eduContentTOCId = readTocId(route);
  if (eduContentTOCId == null) return of(true);

  const includeChildren = !!route.data?.[LEARNING_DOMAINS_INCLUDE_CHILDREN_KEY];
  const includeAncestors = !!route.data?.[LEARNING_DOMAINS_INCLUDE_ANCESTORS_KEY];
  store.dispatch(actions.loadFor({ eduContentTOCId, includeChildren, includeAncestors }));

  // Gate on "attempted" (fetched OR failed) so a failing API call doesn't hang
  // navigation — the page renders with whatever LD data is already cached.
  const query = tocLearningDomainQuery(eduContentTOCId, includeChildren, includeAncestors);
  return store.select(feature.selectIsQueryAttempted(query)).pipe(
    filter(Boolean),
    take(1),
    map(() => true)
  ) as Observable<boolean>;
};

export const resolveLearningDomainsForBook: ResolveFn<boolean> = (route) => {
  const store = inject(Store);

  const eduContentBookId = readBookId(route);
  if (eduContentBookId == null) return of(true);

  store.dispatch(actions.loadForBook({ eduContentBookId }));

  const query = bookLearningDomainQuery(eduContentBookId);
  return store.select(feature.selectIsQueryAttempted(query)).pipe(
    filter(Boolean),
    take(1),
    map(() => true)
  ) as Observable<boolean>;
};
